/**
 * Everything a guide points at: categories, people, sources, claims, rails,
 * countries, and the hub configuration.
 *
 * These are small enough that one file keeps them comparable, and they share the
 * same permission shape. Where the rules file treats a field specially, so does
 * the form: `lastVerifiedAt` on a rail and `verifiedAt`/`reviewedBy` on a claim
 * are a reviewer's signature, and firestore.rules refuses them from an editor.
 */

import { buildCollection, EntityCollection } from "@firecms/core";
import type {
    AvailabilityStatus,
    Category,
    Claim,
    Country,
    HubConfig,
    PaymentRail,
    Person,
    RiskLevel,
    Source,
    SourceType
} from "@zennopay/schema";

import { canEdit, canReview, roleOf } from "../roles";
import { idRefArray, isoDate, mediaRef, stringArray } from "./properties";

const AVAILABILITY: Record<AvailabilityStatus, string> = {
    live: "Live",
    roadmap: "Roadmap",
    informational: "Informational"
};

const SOURCE_TYPES: Record<SourceType, string> = {
    regulator: "Regulator",
    network: "Network",
    partner: "Partner",
    internal: "Internal",
    research: "Research"
};

const RISK_LEVELS: Record<RiskLevel, string> = {
    low: "Low",
    medium: "Medium",
    compliance: "Compliance"
};

/** Admin-only writes, matching `allow write: if isAdmin()` in the rules. */
const adminOnly = ({ authController }: { authController: any }) => {
    const role = roleOf(authController);
    return {
        read: role !== undefined,
        create: role === "admin",
        edit: role === "admin",
        delete: role === "admin"
    };
};

/* -------------------------------------------------------------- categories */

export const categoriesCollection: EntityCollection<Omit<Category, "id">> =
    buildCollection<Omit<Category, "id">>({
        id: "categories",
        path: "categories",
        name: "Categories",
        singularName: "Category",
        icon: "Category",
        group: "Content",
        permissions: adminOnly,
        properties: {
            name: { dataType: "string", name: "Name", validation: { required: true } },
            slug: {
                dataType: "string",
                name: "Slug",
                validation: {
                    required: true,
                    matches: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                    matchesMessage: "Lowercase words separated by single hyphens."
                }
            },
            description: {
                dataType: "string",
                name: "Description",
                multiline: true,
                validation: { required: true }
            },
            sortOrder: { dataType: "number", name: "Sort order", validation: { required: true } },
            icon: { dataType: "string", name: "Icon" },
            isVisible: {
                dataType: "boolean",
                name: "Visible",
                description: "An empty category left visible renders a section with nothing in it.",
                defaultValue: true
            }
        }
    });

/* ------------------------------------------------------------------ people */

const personProperties = {
    name: { dataType: "string" as const, name: "Name", validation: { required: true } },
    role: {
        dataType: "string" as const,
        name: "Role",
        description: "Shown in the byline, e.g. \"Payments research\".",
        validation: { required: true }
    },
    bio: {
        dataType: "string" as const,
        name: "Bio",
        multiline: true,
        validation: { required: true }
    },
    /* The join to a Firebase account, and the only reason a new guide can
       default its author to whoever is signed in. Both are optional and must
       stay so: the team bylines the roadmap deliberately keeps — "Zennopay
       Editorial", "Zennopay Treasury Desk" — have no account behind them, and a
       required field here would mean inventing one.

       `uid` is the identity Firebase actually authenticates and wins when both
       are set; `email` is here because it is the one an admin can fill in
       without going to look up an opaque id. See `derive/people.ts`. */
    uid: {
        dataType: "string" as const,
        name: "Firebase account UID",
        description:
            "Links this byline to a CMS account, so a guide they create is attributed to " +
            "them without being asked. Find it in Admin → User roles. Leave empty for a " +
            "team byline.",
        hideFromCollection: true
    },
    email: {
        dataType: "string" as const,
        name: "Account email",
        email: true,
        description:
            "The address this person signs in with. Used to link the byline to an account " +
            "when the UID is not to hand; the UID wins if both are set, because an email " +
            "address can be reassigned to somebody else.",
        hideFromCollection: true
    },

    /* Same field as a guide's hero: dimensions read from the file, oversized
       uploads downscaled, alt text still required. */
    photo: mediaRef("Photo", "Shown in the byline.", "people"),
    linkedinUrl: { dataType: "string" as const, name: "LinkedIn", url: true },
    expertise: stringArray("Expertise", "Subjects this person is qualified to write on.")
};

export const authorsCollection: EntityCollection<Omit<Person, "id">> =
    buildCollection<Omit<Person, "id">>({
        id: "authors",
        path: "authors",
        name: "Authors",
        singularName: "Author",
        icon: "Person",
        group: "People",
        permissions: adminOnly,
        properties: personProperties
    });

export const reviewersCollection: EntityCollection<Omit<Person, "id">> =
    buildCollection<Omit<Person, "id">>({
        id: "reviewers",
        path: "reviewers",
        name: "Reviewers",
        singularName: "Reviewer",
        icon: "Verified",
        group: "People",
        permissions: adminOnly,
        properties: personProperties
    });

/* ----------------------------------------------------------------- sources */

export const sourcesCollection: EntityCollection<Omit<Source, "id">> =
    buildCollection<Omit<Source, "id">>({
        id: "sources",
        path: "sources",
        name: "Sources",
        singularName: "Source",
        icon: "Link",
        group: "Evidence",
        description: "What we read. A claim is what a sentence in a guide is holding up.",
        textSearchEnabled: true,
        permissions: ({ authController }) => {
            const role = roleOf(authController);
            return {
                read: role !== undefined,
                create: canEdit(role) || canReview(role),
                edit: canEdit(role) || canReview(role),
                delete: role === "admin"
            };
        },
        properties: {
            title: { dataType: "string", name: "Title", validation: { required: true } },
            publisher: { dataType: "string", name: "Publisher", validation: { required: true } },
            url: { dataType: "string", name: "URL", url: true, validation: { required: true } },
            sourceType: {
                dataType: "string",
                name: "Type",
                enumValues: SOURCE_TYPES,
                validation: { required: true }
            },
            publishedAt: isoDate("Published at", { description: "When the source itself was published." }),
            accessedAt: isoDate("Accessed at", { required: true, description: "When we last read it." }),
            notes: { dataType: "string", name: "Notes", multiline: true }
        }
    });

/* ------------------------------------------------------------------ claims */

export const claimsCollection: EntityCollection<Omit<Claim, "id">> =
    buildCollection<Omit<Claim, "id">>({
        id: "claims",
        path: "claims",
        name: "Claims",
        singularName: "Claim",
        icon: "FactCheck",
        group: "Evidence",
        description:
            "A single factual assertion bound to the source that backs it, and to the " +
            "person who last confirmed it.",
        textSearchEnabled: true,
        permissions: ({ authController }) => {
            const role = roleOf(authController);
            return {
                read: role !== undefined,
                create: canEdit(role),
                edit: canEdit(role) || canReview(role),
                delete: role === "admin"
            };
        },
        properties: {
            claimText: {
                dataType: "string",
                name: "Claim",
                description: "One assertion, stated as it appears to the reader.",
                multiline: true,
                validation: { required: true }
            },
            riskLevel: {
                dataType: "string",
                name: "Risk",
                description:
                    "A compliance-risk claim cannot ship unverified, and pulls a named " +
                    "reviewer onto every guide that cites it.",
                enumValues: RISK_LEVELS,
                defaultValue: "medium",
                validation: { required: true }
            },
            sourceId: {
                dataType: "string",
                name: "Source",
                validation: { required: true },
                reference: {
                    dataType: "reference",
                    path: "sources",
                    previewProperties: ["title", "publisher"]
                }
            },

            /* Denormalized from the source at authoring time. The exporter emits
               these into the snapshot so the public page can render a citation
               without a second lookup. */
            sourceUrl: { dataType: "string", name: "Source URL", url: true, validation: { required: true } },
            sourceTitle: { dataType: "string", name: "Source title", validation: { required: true } },
            publisher: { dataType: "string", name: "Publisher", validation: { required: true } },
            sourcePublishedAt: isoDate("Source published at", { hideFromCollection: true }),

            /* The attestation. firestore.rules refuses these from an editor, and
               refuses a reviewer signing as anybody but themselves. */
            verifiedAt: ({ authController }) => ({
                ...isoDate("Verified at", {
                    description: "A reviewer's signature. Only a reviewer may set it."
                }),
                readOnly: !canReview(roleOf(authController))
            }),
            reviewedBy: ({ authController }) => ({
                dataType: "string" as const,
                name: "Reviewed by",
                email: true,
                description: "Must be the signed-in reviewer's own email address.",
                readOnly: !canReview(roleOf(authController))
            })
        }
    });

/* ----------------------------------------------------------- rails, countries */

export const paymentRailsCollection: EntityCollection<Omit<PaymentRail, "id">> =
    buildCollection<Omit<PaymentRail, "id">>({
        id: "paymentRails",
        path: "paymentRails",
        name: "Payment rails",
        singularName: "Payment rail",
        icon: "AccountTree",
        group: "Coverage",
        permissions: ({ authController }) => {
            const role = roleOf(authController);
            return {
                read: role !== undefined,
                create: canEdit(role),
                edit: canEdit(role) || canReview(role),
                delete: role === "admin"
            };
        },
        properties: {
            name: { dataType: "string", name: "Name", validation: { required: true } },
            slug: {
                dataType: "string",
                name: "Slug",
                validation: {
                    required: true,
                    matches: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                    matchesMessage: "Lowercase words separated by single hyphens."
                }
            },
            shortDescription: {
                dataType: "string",
                name: "Short description",
                multiline: true,
                validation: { required: true }
            },
            officialSourceUrl: {
                dataType: "string",
                name: "Official source",
                url: true,
                description: "The operator or regulator page this record is taken from.",
                validation: { required: true }
            },
            availabilityStatus: {
                dataType: "string",
                name: "Availability",
                enumValues: AVAILABILITY,
                validation: { required: true }
            },
            countryIds: idRefArray("Countries", "countries", { previewProperties: ["name"] }),
            relatedGuideIds: idRefArray("Related guides", "guides", { previewProperties: ["title"] }),
            lastVerifiedAt: ({ authController }) => ({
                ...isoDate("Last verified at", {
                    description: "A reviewer's signature that this rail is still as described."
                }),
                readOnly: !canReview(roleOf(authController))
            })
        }
    });

export const countriesCollection: EntityCollection<Omit<Country, "id">> =
    buildCollection<Omit<Country, "id">>({
        id: "countries",
        path: "countries",
        name: "Countries",
        singularName: "Country",
        icon: "Public",
        group: "Coverage",
        permissions: ({ authController }) => {
            const role = roleOf(authController);
            return {
                read: role !== undefined,
                create: canEdit(role),
                edit: canEdit(role) || canReview(role),
                delete: role === "admin"
            };
        },
        properties: {
            name: { dataType: "string", name: "Name", validation: { required: true } },
            slug: {
                dataType: "string",
                name: "Slug",
                validation: {
                    required: true,
                    matches: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
                    matchesMessage: "Lowercase words separated by single hyphens."
                }
            },
            currencyCodes: stringArray("Currencies", "ISO 4217 codes, e.g. THB."),
            coverageStatus: {
                dataType: "string",
                name: "Coverage",
                enumValues: AVAILABILITY,
                validation: { required: true }
            },
            paymentRailIds: idRefArray("Payment rails", "paymentRails", { previewProperties: ["name"] }),
            relatedGuideIds: idRefArray("Related guides", "guides", { previewProperties: ["title"] }),
            lastVerifiedAt: ({ authController }) => ({
                ...isoDate("Last verified at", {
                    description: "A reviewer's signature that this coverage is still accurate."
                }),
                readOnly: !canReview(roleOf(authController))
            })
        }
    });

/* --------------------------------------------------------------- hub config */

export const hubConfigCollection: EntityCollection<HubConfig> = buildCollection<HubConfig>({
    id: "hubConfig",
    path: "hubConfig",
    name: "Hub configuration",
    singularName: "Hub configuration",
    icon: "Settings",
    group: "Content",
    description:
        "The guides index page. A singleton: the exporter reads the document " +
        "with id \"guides\", so do not create a second one.",
    permissions: adminOnly,
    properties: {
        title: { dataType: "string", name: "Title", validation: { required: true } },
        description: {
            dataType: "string",
            name: "Description",
            multiline: true,
            validation: { required: true }
        },
        featuredGuideId: {
            dataType: "string",
            name: "Featured guide",
            reference: { dataType: "reference", path: "guides", previewProperties: ["title"] }
        },
        ctaLabel: { dataType: "string", name: "CTA label", validation: { required: true } },
        ctaHref: { dataType: "string", name: "CTA link", url: true, validation: { required: true } },
        bottomCtaTitle: { dataType: "string", name: "Bottom CTA title", validation: { required: true } },
        bottomCtaBody: {
            dataType: "string",
            name: "Bottom CTA body",
            multiline: true,
            validation: { required: true }
        },
        bottomCtaLabel: { dataType: "string", name: "Bottom CTA label", validation: { required: true } },
        bottomCtaHref: {
            dataType: "string",
            name: "Bottom CTA link",
            url: true,
            validation: { required: true }
        }
    }
});
